/**
 * Shared fundamental metric definitions and correlation helpers.
 * Used by StockChart (Fundamentals tab) and Prediction Lab so both show the same metrics and correlations.
 */
import { alignDateToInterval, findNearestPriceValue, forwardFillIncomeField, getFundDate } from "../indicators/engine";
import { forwardEstimateRows } from "./analystForecasts";

/**
 * FMP key-metrics fields stored as fractions (e.g. 0.39). Chart/display unit is percent (39).
 * YoY growth metrics are already computed as percent and are not in this set.
 */
export const FMP_RATIO_PERCENT_FIELDS = new Set([
  "grossProfitMargin",
  "operatingProfitMargin",
  "netProfitMargin",
  "returnOnCapitalEmployed",
  "returnOnAssets",
]);

/** Scale raw fundamental field values for chart display (billions, millions, percent). */
export function scaleFundamentalFieldValue(field, v) {
  if (v == null || !Number.isFinite(Number(v))) return undefined;
  let n = Number(v);
  if (["revenue", "freeCashFlow", "operatingCashFlow", "operatingIncome", "totalDebt"].includes(field)) n = n / 1e9;
  else if (field === "weightedAverageShsOut") n = n / 1e6;
  else if (FMP_RATIO_PERCENT_FIELDS.has(field)) n = n * 100;
  return n;
}

export const METRIC_PERIOD_QUARTER = "quarter";
export const METRIC_PERIOD_YEAR = "year";

/** Map quarterly statement source keys to annual API arrays. */
const ANNUAL_SOURCE_BY_QUARTER = {
  income: "annualIncome",
  cashflow: "annualCashflow",
  metrics: "annualMetrics",
  balanceSheet: "annualBalanceSheet",
};

/** Total interest-bearing debt from an FMP balance-sheet row. */
export function resolveTotalDebt(row) {
  if (!row) return null;
  if (row.totalDebt != null && Number.isFinite(Number(row.totalDebt))) return Number(row.totalDebt);
  const lt = row.longTermDebt != null && Number.isFinite(Number(row.longTermDebt)) ? Number(row.longTermDebt) : null;
  const st = row.shortTermDebt != null && Number.isFinite(Number(row.shortTermDebt)) ? Number(row.shortTermDebt) : null;
  if (lt != null || st != null) return (lt || 0) + (st || 0);
  return null;
}

/** Book equity from an FMP balance-sheet row. */
export function resolveTotalEquity(row) {
  if (!row) return null;
  const v =
    row.totalStockholdersEquity ??
    row.totalShareholdersEquity ??
    row.totalEquity ??
    row.stockholdersEquity;
  return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
}

/**
 * Chart value for balance-sheet metrics (Debt in $B; Debt/Equity and Debt/Assets as ratios).
 * @param {"totalDebt"|"debtToEquity"|"debtToAssets"} field
 */
export function balanceSheetMetricValue(row, field) {
  const debt = resolveTotalDebt(row);
  if (debt == null) return undefined;
  if (field === "totalDebt") return debt / 1e9;
  if (field === "debtToEquity") {
    const equity = resolveTotalEquity(row);
    if (equity == null || !(Math.abs(equity) > 0)) return undefined;
    const r = debt / equity;
    return Number.isFinite(r) ? r : undefined;
  }
  if (field === "debtToAssets") {
    const assets = row?.totalAssets;
    if (assets == null || !Number.isFinite(Number(assets)) || !(Number(assets) > 0)) return undefined;
    const r = debt / Number(assets);
    return Number.isFinite(r) ? r : undefined;
  }
  return undefined;
}

export function resolveMetricStatementRows(fundamentals, source, period = METRIC_PERIOD_QUARTER) {
  if (!fundamentals || !source || source === "none") return [];
  if (period === METRIC_PERIOD_YEAR) {
    const annualKey = ANNUAL_SOURCE_BY_QUARTER[source];
    if (annualKey) {
      const annual = fundamentals[annualKey];
      if (Array.isArray(annual) && annual.length) return annual;
    }
  }
  return fundamentals[source] || [];
}

/** Pull estimated revenue/EPS from an FMP analyst-estimate row. */
export function estimateValueFromAnalystRow(row, type = "revenue") {
  if (!row) return null;
  if (type === "eps") {
    const v =
      row.estimatedEpsAvg ??
      row.estimatedEPSAvg ??
      row.estimatedEps ??
      row.estimatedEPS ??
      row.epsEstimate;
    return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
  }
  const v =
    row.estimatedRevenueAvg ??
    row.estimatedRevenue ??
    row.revenueEstimate ??
    row.estimatedRevenueHigh;
  return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
}

/**
 * Annual estimate slots from FMP annual analyst-estimates rows.
 * Chart times use report/filing-style dates (same as annual actuals), not bare fiscal year-end.
 */
export function buildAnnualEstimateChartSlots(
  fundamentals,
  type = "revenue",
  interval = "quarter",
  lagDays = 45,
  minPxTime = 0
) {
  const byFiscal = new Map();
  const fiscalToReport = buildFiscalToReportDateMap(fundamentals);
  const medianLag = medianFiscalToReportLagDays(fiscalToReport);
  for (const row of fundamentals?.annualAnalystEstimates || []) {
    const estimate = estimateValueFromAnalystRow(row, type);
    if (estimate == null) continue;
    const fiscalEnd = String(row.fiscalDateEnding || row.date || "").slice(0, 10);
    if (!fiscalEnd) continue;
    const reportDate = estimateReportDateForFiscalEnd(fiscalEnd, fiscalToReport, medianLag);
    const t = reportDateToChartTime(reportDate, interval, lagDays);
    if (t == null) continue;
    if (minPxTime && t < minPxTime) continue;
    byFiscal.set(fiscalEnd, {
      fiscalEnd,
      estimate,
      time: t,
      reportDate,
      fromEarnings: false,
    });
  }
  return Array.from(byFiscal.values()).sort((a, b) => a.time - b.time);
}


/** Same as StockChart METRIC_OPTIONS – fundamental metrics available for correlation and ML features. */
export const METRIC_OPTIONS = {
  none:          { label: "None",                         source: "none" },
  revenue:       { label: "Revenue",                      source: "income",    field: "revenue",                  unit: "$B" },
  revenue_yoy_forecast: { label: "YoY Rev growth (Forecast)", source: "revenue_yoy_forecast",                   unit: "%"  },
  eps:           { label: "EPS",                          source: "income",    field: "eps",                      unit: "$"  },
  eps_estimate:  { label: "EPS (Forecast)",               source: "combined", field: "eps",                      unit: "$"  },
  revenue_estimate: { label: "Revenue (Forecast)",        source: "combined", field: "revenue",                  unit: "$B" },
  fcf:           { label: "Free Cash Flow",               source: "cashflow", field: "freeCashFlow",             unit: "$M" },
  ocf:           { label: "Operating Cash Flow",          source: "cashflow", field: "operatingCashFlow",       unit: "$M" },
  roic:          { label: "ROIC",                         source: "metrics", field: "returnOnCapitalEmployed",  unit: "%"  },
  gross_margin:  { label: "Gross Margin",                 source: "metrics", field: "grossProfitMargin",         unit: "%"  },
  operating_margin: { label: "Operating Margin",          source: "metrics",  field: "operatingProfitMargin",    unit: "%"  },
  net_profit_margin: { label: "Net Profit Margin",        source: "metrics",  field: "netProfitMargin",          unit: "%"  },
  cashpershare:  { label: "Cash per share",               source: "metrics", field: "cashPerShare",             unit: "$"  },
  roa:           { label: "ROA",                          source: "metrics",  field: "returnOnAssets",           unit: "%"  },
  dcf:           { label: "DCF Valuation (per share)",     source: "dcf",     field: "dcfPerShare",             unit: "$"  },
  graham_intrinsic: { label: "Graham intrinsic value",     source: "graham",  field: "",                        unit: "$"  },
  current_ratio: { label: "Current Ratio",               source: "metrics",  field: "currentRatio",              unit: ""   },
  operating_income: { label: "Operating Income",          source: "income",  field: "operatingIncome",           unit: "$B" },
  shares_outstanding: { label: "Shares Outstanding",      source: "income",   field: "weightedAverageShsOut",    fieldAlt: "weightedAverageShsOutDil", unit: "M" },
  debt:          { label: "Debt",                         source: "balanceSheet", field: "totalDebt",            unit: "$B" },
  debt_equity:   { label: "Debt / Equity",                source: "balanceSheet", field: "debtToEquity",         unit: "x"  },
  debt_assets:   { label: "Debt / Assets",                source: "balanceSheet", field: "debtToAssets",         unit: "x"  },
};

/**
 * Parse /api/macro/aaa-yield response into sorted { time, value }[] (value = yield %).
 */
export function parseAaaYieldApiResponse(res) {
  const fallbackPct = Number(res?.fallbackPct) > 0 ? Number(res.fallbackPct) : 5;
  const pts = (res?.points || [])
    .map((r) => {
      const d = r?.date;
      if (!d) return null;
      const s = String(d).slice(0, 10);
      const [Y, M, D] = s.split("-").map(Number);
      if (!Number.isFinite(Y) || !Number.isFinite(M)) return null;
      const dd = Number.isFinite(D) ? D : 1;
      const time = Date.UTC(Y, M - 1, dd) / 1000;
      const value = Number(r.value);
      if (!Number.isFinite(value) || value <= 0) return null;
      return { time, value };
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);
  return { points: pts, fallbackPct: fallbackPct };
}

function dilutedEpsFromIncomeRow(row) {
  const v =
    row?.epsDiluted ??
    row?.epsdiluted ??
    row?.dilutedEPS ??
    row?.eps;
  if (v == null || !Number.isFinite(Number(v))) return null;
  return Number(v);
}

function fiscalYearFromIncomeRow(row) {
  if (row?.calendarYear != null && Number.isFinite(Number(row.calendarYear)))
    return Number(row.calendarYear);
  const fe = row?.fiscalDateEnding || row?.date;
  if (!fe || typeof fe !== "string") return null;
  const y = parseInt(fe.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
}

/** One row per fiscal quarter (latest filing wins). */
function latestIncomeByQuarter(rows) {
  const m = new Map();
  for (const row of rows || []) {
    const qk = row.fiscalDateEnding || row.date;
    if (!qk) continue;
    const fd = new Date(row.fillingDate || row.acceptedDate || row.date).getTime();
    const prev = m.get(String(qk));
    if (!prev) {
      m.set(String(qk), row);
      continue;
    }
    const pfd = new Date(prev.fillingDate || prev.acceptedDate || prev.date).getTime();
    if (fd >= pfd) m.set(String(qk), row);
  }
  return Array.from(m.values()).sort(
    (a, b) =>
      new Date(a.fillingDate || a.acceptedDate || a.date) -
      new Date(b.fillingDate || b.acceptedDate || b.date)
  );
}

function aaaYieldPercentAt(sortedPts, tSec, fallbackPct) {
  if (!sortedPts?.length) return fallbackPct;
  let y = fallbackPct;
  for (const p of sortedPts) {
    if (p.time <= tSec) y = p.value;
    else break;
  }
  return y;
}

/**
 * Add whole days to a calendar YYYY-MM-DD using UTC midnight steps.
 * Matches how OHLC dates are turned into epoch times in StockChart (Date.UTC on the same string),
 * and avoids local setDate + toISOString() shifting the day vs the price series.
 */
function calendarYmdPlusDays(ymdStr, deltaDays) {
  const s = String(ymdStr || "").slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const Y = Number(m[1]);
  const Mo = Number(m[2]);
  const D = Number(m[3]);
  if (![Y, Mo, D].every((n) => Number.isFinite(n))) return null;
  const ms = Date.UTC(Y, Mo - 1, D) + Number(deltaDays) * 86400000;
  const u = new Date(ms);
  const yy = u.getUTCFullYear();
  const mm = String(u.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(u.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * V = (EPS * (8.5 + 2*g) * 4.4) / Y
 * EPS = Average2 (mean diluted EPS in refYear-2..refYear, using available years)
 * g = (Average2/Average1)^(1/10) - 1; Average1 = mean in refYear-12..refYear-10 (partial years ok)
 * Y = Aaa corporate bond yield (%), last known as of chart date
 */
export function buildGrahamIntrinsicPoints(fundamentals, options = {}) {
  const {
    interval = "quarter",
    lagDays = 45,
    minPxTime = 0,
    maxPxTime = null,
    aaaYieldPoints = [],
    fallbackYieldPercent = 5,
  } = options;

  const rows = latestIncomeByQuarter(fundamentals.income || []);
  const quarters = new Map();
  const out = [];

  for (const row of rows) {
    const qk = row.fiscalDateEnding || row.date;
    if (!qk) continue;
    const fy = fiscalYearFromIncomeRow(row);
    const eps = dilutedEpsFromIncomeRow(row);
    if (fy == null || eps == null) continue;
    quarters.set(String(qk), { fy, eps });

    const accumulated = Array.from(quarters.values());
    const refYear = Math.max(...accumulated.map((x) => x.fy));
    const w2 = accumulated
      .filter((x) => x.fy >= refYear - 2 && x.fy <= refYear)
      .map((x) => x.eps);
    const w1 = accumulated
      .filter((x) => x.fy >= refYear - 12 && x.fy <= refYear - 10)
      .map((x) => x.eps);
    if (w2.length < 1 || w1.length < 1) continue;

    const avg2 = w2.reduce((s, v) => s + v, 0) / w2.length;
    const avg1 = w1.reduce((s, v) => s + v, 0) / w1.length;
    if (!(avg1 > 0 && avg2 > 0)) continue;

    const g = Math.pow(avg2 / avg1, 1 / 10) - 1;
    if (!Number.isFinite(g)) continue;

    const d = new Date(row.fillingDate || row.acceptedDate || row.date);
    d.setDate(d.getDate() + lagDays);
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    const [Y, M, D] = key.split("-").map(Number);
    const time = Date.UTC(Y, M - 1, D) / 1000;
    if (time < minPxTime) continue;

    const Ypct = aaaYieldPercentAt(aaaYieldPoints, time, fallbackYieldPercent);
    if (!(Ypct > 0)) continue;

    const V = (avg2 * (8.5 + 2 * g) * 4.4) / Ypct;
    if (!Number.isFinite(V)) continue;
    out.push({ time, value: V });
  }

  const byTime = new Map();
  for (const p of out) {
    if (p && Number.isFinite(p.time) && Number.isFinite(p.value)) byTime.set(p.time, p.value);
  }
  return Array.from(byTime.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([time, value]) => ({ time, value }))
    .filter((p) => {
      if (p.time < minPxTime) return false;
      if (maxPxTime != null && Number.isFinite(maxPxTime) && p.time > maxPxTime) return false;
      return true;
    });
}

function priorYearFiscalDate(fiscalEndStr) {
  const s = String(fiscalEndStr || "").slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return `${Number(m[1]) - 1}-${m[2]}-${m[3]}`;
}

/**
 * Chart time for a fiscal period-end YYYY-MM-DD (+ lag), aligned to the chart interval.
 */
export function fiscalEndToChartTime(fiscalEndStr, interval = "quarter", lagDays = 0) {
  const fe = String(fiscalEndStr || "").slice(0, 10);
  const shifted = calendarYmdPlusDays(fe, lagDays);
  if (!shifted) return null;
  const key = alignDateToInterval(shifted, interval);
  const [Y, M, D] = key.split("-").map(Number);
  if (![Y, M, D].every((n) => Number.isFinite(n))) return null;
  return Date.UTC(Y, M - 1, D) / 1000;
}

/**
 * Same dating convention as statement actuals: report/filing calendar date + lagDays,
 * then align to the chart interval. Used so forecasts sit on the same x-axis as actuals.
 */
export function reportDateToChartTime(dateStr, interval = "quarter", lagDays = 0) {
  const s = String(dateStr || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return null;
  d.setDate(d.getDate() + lagDays);
  const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
  const [Y, M, D] = key.split("-").map(Number);
  if (![Y, M, D].every((n) => Number.isFinite(n))) return null;
  return Date.UTC(Y, M - 1, D) / 1000;
}

/** fiscalDateEnding → earnings report / filing date (YYYY-MM-DD). */
function buildFiscalToReportDateMap(fundamentals) {
  const map = new Map();
  for (const e of fundamentals?.earnings || fundamentals?.earningsHistory || []) {
    const fe = String(e.fiscalDateEnding || "").slice(0, 10);
    const report = String(e.date || "").slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(fe) && /^\d{4}-\d{2}-\d{2}$/.test(report)) {
      map.set(fe, report);
    }
  }
  for (const row of [
    ...(fundamentals?.income || []),
    ...(fundamentals?.annualIncome || []),
  ]) {
    const fe = String(row.fiscalDateEnding || row.date || "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fe) || map.has(fe)) continue;
    const report = String(row.fillingDate || row.acceptedDate || "").slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(report)) map.set(fe, report);
  }
  return map;
}

/** Median days from fiscal period-end to report/filing date (fallback when no scheduled report). */
function medianFiscalToReportLagDays(fiscalToReport) {
  const lags = [];
  for (const [fe, report] of fiscalToReport.entries()) {
    const a = Date.parse(`${fe}T00:00:00Z`);
    const b = Date.parse(`${report}T00:00:00Z`);
    if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
    const lag = Math.round((b - a) / 86400000);
    if (lag >= 0 && lag <= 120) lags.push(lag);
  }
  if (!lags.length) return 30;
  lags.sort((x, y) => x - y);
  return lags[Math.floor(lags.length / 2)];
}

function estimateReportDateForFiscalEnd(fiscalEnd, fiscalToReport, medianLagDays) {
  const fe = String(fiscalEnd || "").slice(0, 10);
  if (fiscalToReport.has(fe)) return fiscalToReport.get(fe);
  // Nearest known fiscal end within ~45 days (date string drift across sources)
  const feMs = Date.parse(`${fe}T00:00:00Z`);
  if (Number.isFinite(feMs)) {
    let best = null;
    let bestDist = Infinity;
    for (const [k, report] of fiscalToReport.entries()) {
      const kMs = Date.parse(`${k}T00:00:00Z`);
      if (!Number.isFinite(kMs)) continue;
      const dist = Math.abs(kMs - feMs);
      if (dist < bestDist && dist <= 45 * 86400000) {
        bestDist = dist;
        best = report;
      }
    }
    if (best) return best;
  }
  return calendarYmdPlusDays(fe, medianLagDays);
}

/**
 * Unified estimate slots aligned to report/announcement dates (same as actual EPS/revenue).
 * Earnings-calendar `date` is the report date; forward analyst rows use that calendar when
 * available, otherwise fiscal-end + typical report lag from history.
 * Earnings-calendar values win when both exist for the same fiscal quarter.
 *
 * @param {"revenue"|"eps"} type
 */
export function buildEstimateChartSlots(
  fundamentals,
  type = "revenue",
  interval = "quarter",
  lagDays = 45,
  minPxTime = 0
) {
  const byFiscal = new Map();
  const fiscalToReport = buildFiscalToReportDateMap(fundamentals);
  const medianLag = medianFiscalToReportLagDays(fiscalToReport);
  const forecastBox =
    type === "eps"
      ? fundamentals?.analystForecasts?.eps
      : fundamentals?.analystForecasts?.revenue;
  const forwardRows = forwardEstimateRows(forecastBox);

  for (const item of forwardRows) {
    if (!item?.date && !item?.fiscalDateEnding) continue;
    if (item.estimate == null || !Number.isFinite(Number(item.estimate))) continue;
    const fiscalEnd = String(item.fiscalDateEnding || item.date).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fiscalEnd)) continue;
    const reportDate = estimateReportDateForFiscalEnd(fiscalEnd, fiscalToReport, medianLag);
    const t = reportDateToChartTime(reportDate, interval, lagDays);
    if (t == null) continue;
    if (minPxTime && t < minPxTime) continue;
    byFiscal.set(fiscalEnd, {
      fiscalEnd,
      estimate: Number(item.estimate),
      time: t,
      reportDate,
      fromEarnings: false,
    });
  }

  const src = fundamentals?.earnings || fundamentals?.earningsHistory || [];
  for (const e of src) {
    const est =
      type === "eps"
        ? e.epsEstimated
        : e.revenueEstimated;
    if (est == null || !Number.isFinite(est)) continue;
    // FMP earnings calendar: `date` = report/announcement (aligns with actuals),
    // `fiscalDateEnding` = quarter end.
    const fiscalEnd = String(e.fiscalDateEnding || e.date || "").slice(0, 10);
    if (!fiscalEnd) continue;
    const reportDate = String(e.date || e.fiscalDateEnding || "").slice(0, 10);
    const t = reportDateToChartTime(reportDate, interval, lagDays);
    if (t == null) continue;
    if (minPxTime && t < minPxTime) continue;
    byFiscal.set(fiscalEnd, {
      fiscalEnd,
      estimate: est,
      time: t,
      reportDate,
      fromEarnings: true,
    });
  }

  return Array.from(byFiscal.values()).sort((a, b) => a.time - b.time);
}

/** @deprecated Prefer buildEstimateChartSlots(..., "revenue", ...) */
export function buildRevenueEstimateChartSlots(fundamentals, interval = "quarter", lagDays = 45, minPxTime = 0) {
  return buildEstimateChartSlots(fundamentals, "revenue", interval, lagDays, minPxTime);
}

/** EPS estimate slots (fiscal-end cadence). */
export function buildEpsEstimateChartSlots(fundamentals, interval = "quarter", lagDays = 45, minPxTime = 0) {
  return buildEstimateChartSlots(fundamentals, "eps", interval, lagDays, minPxTime);
}

function hasReportedActualRevenue(fiscalEnd, actualRows) {
  const fe = String(fiscalEnd).slice(0, 10);
  return actualRows.some((r) => r.fe === fe);
}

function buildQuarterlyActualRevenue(fundamentals) {
  const rows = [];
  const seen = new Set();
  const add = (fiscalEnd, revenue) => {
    const fe = String(fiscalEnd || "").slice(0, 10);
    if (!fe || seen.has(fe)) return;
    if (revenue == null || !Number.isFinite(revenue) || revenue <= 0) return;
    const ms = Date.parse(`${fe}T00:00:00Z`);
    if (!Number.isFinite(ms)) return;
    seen.add(fe);
    rows.push({ fe, revenue, ms });
  };

  for (const row of forwardFillIncomeField(fundamentals?.income || [], "revenue")) {
    add(row.fiscalDateEnding || row.date, row.revenue);
  }
  for (const e of fundamentals?.earnings || fundamentals?.earningsHistory || []) {
    add(e.fiscalDateEnding || e.date, e.revenue);
  }
  rows.sort((a, b) => a.ms - b.ms);
  return rows;
}

/** Actual or estimated revenue for the same fiscal quarter one year earlier (tolerant of ±92d date drift). */
function priorYearSameQuarterRevenue(fiscalEnd, actualRows, estimateRows = []) {
  const target = priorYearFiscalDate(fiscalEnd);
  if (!target) return null;

  if (actualRows?.length) {
    const exact = actualRows.find((r) => r.fe === target);
    if (exact) return exact.revenue;
  }

  if (estimateRows?.length) {
    const exactEst = estimateRows.find((r) => r.fe === target);
    if (exactEst) return exactEst.revenue;
  }

  const targetMs = Date.parse(`${target}T00:00:00Z`);
  if (!Number.isFinite(targetMs)) return null;

  const nearestIn = (rows) => {
    let best = null;
    let bestDist = Infinity;
    for (const r of rows || []) {
      const dist = Math.abs(r.ms - targetMs);
      if (dist < bestDist) {
        bestDist = dist;
        best = r.revenue;
      }
    }
    return bestDist <= 92 * 86400000 ? best : null;
  };

  return nearestIn(actualRows) ?? nearestIn(estimateRows);
}

/**
 * Actual YoY revenue growth.
 * Quarterly: current quarter vs same quarter prior year (i−4).
 * Yearly: current fiscal year vs prior fiscal year (i−1).
 */
export function buildRevenueYoYPoints(income, interval, lagDays, minPxTime, period = METRIC_PERIOD_QUARTER) {
  const filled = forwardFillIncomeField(income, "revenue");
  const lookback = period === METRIC_PERIOD_YEAR ? 1 : 4;
  const pts = [];
  for (let i = lookback; i < filled.length; i++) {
    const cur = filled[i].revenue;
    const prev = filled[i - lookback].revenue;
    if (cur == null || prev == null || !Number.isFinite(cur) || !Number.isFinite(prev) || prev <= 0) continue;
    const yoyPct = (cur / prev - 1) * 100;
    const d = getFundDate(filled[i]);
    d.setDate(d.getDate() + lagDays);
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    const [Y, M, D] = key.split("-").map(Number);
    const t = Date.UTC(Y, M - 1, D) / 1000;
    if (minPxTime != null && Number.isFinite(minPxTime) && t < minPxTime) continue;
    pts.push({ time: t, value: yoyPct });
  }
  return pts.sort((a, b) => a.time - b.time);
}

/**
 * Forward annual YoY revenue growth from annual analyst estimates.
 */
export function buildRevenueYoYForwardPointsAnnual(fundamentals, options = {}) {
  const {
    interval = "quarter",
    lagDays = 45,
    minPxTime = 0,
    maxPxTime = null,
    minForwardTime = null,
  } = options;

  const actualRows = [];
  for (const row of forwardFillIncomeField(fundamentals?.annualIncome || [], "revenue")) {
    const fe = String(row.fiscalDateEnding || row.date || "").slice(0, 10);
    if (!fe || row.revenue == null || !Number.isFinite(row.revenue) || !(row.revenue > 0)) continue;
    const ms = Date.parse(`${fe}T00:00:00Z`);
    if (!Number.isFinite(ms)) continue;
    actualRows.push({ fe, revenue: row.revenue, ms });
  }
  actualRows.sort((a, b) => a.ms - b.ms);

  const slots = buildAnnualEstimateChartSlots(fundamentals, "revenue", interval, lagDays, minPxTime);
  const estimateRows = slots
    .filter((s) => s.estimate != null && Number.isFinite(s.estimate) && s.estimate > 0)
    .map((s) => ({
      fe: s.fiscalEnd,
      revenue: s.estimate,
      ms: Date.parse(`${s.fiscalEnd}T00:00:00Z`),
    }))
    .filter((r) => Number.isFinite(r.ms))
    .sort((a, b) => a.ms - b.ms);

  const lookupPrior = (fiscalEnd) => {
    const target = priorYearFiscalDate(fiscalEnd);
    if (!target) return null;
    const targetMs = Date.parse(`${target}T00:00:00Z`);
    if (!Number.isFinite(targetMs)) return null;
    const nearestIn = (rows) => {
      let best = null;
      let bestDist = Infinity;
      for (const r of rows || []) {
        const dist = Math.abs(r.ms - targetMs);
        if (dist < bestDist) {
          bestDist = dist;
          best = r.revenue;
        }
      }
      return bestDist <= 200 * 86400000 ? best : null;
    };
    return nearestIn(actualRows) ?? nearestIn(estimateRows);
  };

  const estPts = [];
  for (const slot of slots) {
    if (!(slot.estimate > 0)) continue;
    if (minForwardTime != null && Number.isFinite(minForwardTime) && slot.time <= minForwardTime) continue;
    if (maxPxTime != null && Number.isFinite(maxPxTime) && slot.time > maxPxTime) continue;
    const prevRev = lookupPrior(slot.fiscalEnd);
    if (prevRev == null || !(prevRev > 0)) continue;
    const yoy = (slot.estimate / prevRev - 1) * 100;
    if (!Number.isFinite(yoy)) continue;
    if (minPxTime != null && Number.isFinite(minPxTime) && slot.time < minPxTime) continue;
    estPts.push({ time: slot.time, value: yoy });
  }
  return estPts.sort((a, b) => a.time - b.time);
}

/**
 * Forward YoY revenue growth from analyst consensus (up to 8 quarters / ~2y).
 * Year-1 quarters: estimate vs same-quarter actual one year ago.
 * Year-2 quarters: estimate vs same-quarter year-1 forecast revenue.
 */
export function buildRevenueYoYForwardPoints(fundamentals, options = {}) {
  const {
    interval = "quarter",
    lagDays = 45,
    minPxTime = 0,
    maxPxTime = null,
    minForwardTime = null,
  } = options;

  const actualRows = buildQuarterlyActualRevenue(fundamentals);
  const forwardFiscalEnds = new Set(
    forwardEstimateRows(fundamentals?.analystForecasts?.revenue).map((r) =>
      String(r.date).slice(0, 10)
    )
  );
  const slots = buildRevenueEstimateChartSlots(fundamentals, interval, lagDays, minPxTime);

  // Year-2 YoY needs year-1 forecast revenue as the denominator when actuals don't exist yet.
  const estimateRows = [];
  const seenEst = new Set();
  for (const slot of slots) {
    const fe = String(slot.fiscalEnd || "").slice(0, 10);
    if (!fe || seenEst.has(fe)) continue;
    if (slot.estimate == null || !Number.isFinite(slot.estimate) || !(slot.estimate > 0)) continue;
    const ms = Date.parse(`${fe}T00:00:00Z`);
    if (!Number.isFinite(ms)) continue;
    seenEst.add(fe);
    estimateRows.push({ fe, revenue: slot.estimate, ms });
  }
  estimateRows.sort((a, b) => a.ms - b.ms);

  const keepPoint = (t, value) => {
    if (t == null || !Number.isFinite(t) || value == null || !Number.isFinite(value)) return null;
    if (minPxTime != null && Number.isFinite(minPxTime) && t < minPxTime) return null;
    if (maxPxTime != null && Number.isFinite(maxPxTime) && t > maxPxTime) return null;
    if (minForwardTime != null && Number.isFinite(minForwardTime) && t <= minForwardTime) return null;
    return { time: t, value };
  };

  const estPts = [];
  for (const slot of slots) {
    const inAnalystForward = forwardFiscalEnds.has(slot.fiscalEnd);
    const upcomingFromEarnings =
      slot.fromEarnings && !hasReportedActualRevenue(slot.fiscalEnd, actualRows);
    if (!inAnalystForward && !upcomingFromEarnings) continue;

    const prevRev = priorYearSameQuarterRevenue(slot.fiscalEnd, actualRows, estimateRows);
    if (prevRev == null || !(prevRev > 0) || !(slot.estimate > 0)) continue;

    const yoy = (slot.estimate / prevRev - 1) * 100;
    if (!Number.isFinite(yoy)) continue;

    const pt = keepPoint(slot.time, yoy);
    if (pt) estPts.push(pt);
  }

  const byTime = new Map();
  for (const p of estPts.sort((a, b) => a.time - b.time)) byTime.set(p.time, p.value);
  return Array.from(byTime.entries()).map(([time, value]) => ({ time, value }));
}

/** Metric keys that are used for correlation (excludes none and dcf in the table; dcf can be included if data exists) */
export const CORRELATION_METRIC_KEYS = Object.keys(METRIC_OPTIONS).filter(
  (k) => k !== "none" && k !== "dcf"
);

/**
 * Build time-series points for one fundamental metric from raw API data.
 * @param {string} metricKey - key in METRIC_OPTIONS
 * @param {{ income?: any[], cashflow?: any[], balanceSheet?: any[], metrics?: any[], earningsHistory?: any[], earnings?: any[], dcf?: any[] }} fundamentals
 * @param {{ interval?: string, lagDays?: number, minPxTime?: number }} options
 * @returns {{ time: number, value: number }[]}
 */
export function buildMetricPointsForKey(metricKey, fundamentals, options = {}) {
  const {
    interval = "quarter",
    lagDays = 45,
    minPxTime = 0,
    aaaYieldPoints = [],
    fallbackYieldPercent = 5,
    period = METRIC_PERIOD_QUARTER,
  } = options;
  const opt = METRIC_OPTIONS[metricKey];
  if (!opt || opt.source === "none") return [];
  const isAnnual = period === METRIC_PERIOD_YEAR;

  if (metricKey === "revenue_yoy_forecast" || metricKey === "revenue_yoy") {
    const incomeRows = isAnnual
      ? (fundamentals?.annualIncome || fundamentals?.income || [])
      : (fundamentals?.income || []);
    return buildRevenueYoYPoints(incomeRows, interval, lagDays, minPxTime, period);
  }

  if (opt.source === "graham") {
    return buildGrahamIntrinsicPoints(fundamentals, {
      interval,
      lagDays,
      minPxTime,
      aaaYieldPoints,
      fallbackYieldPercent,
    });
  }

  function cropToPrice(arr = []) {
    return (arr || []).filter((p) => p && Number.isFinite(p.time) && p.time >= minPxTime);
  }

  function shiftAndMap(arr, valueSelector) {
    return cropToPrice(
      (arr || [])
        .map((e) => {
          const d = new Date(e.fillingDate || e.acceptedDate || e.date);
          d.setDate(d.getDate() + lagDays);
          const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
          const [Y, M, D] = key.split("-").map(Number);
          const time = Date.UTC(Y, M - 1, D) / 1000;
          const val = valueSelector(e);
          return val != null && isFinite(val) ? { time, value: val } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.time - b.time)
    );
  }

  if (metricKey === "eps" || metricKey === "revenue") {
    if (isAnnual) {
      const isRevenue = metricKey === "revenue";
      const raw = resolveMetricStatementRows(fundamentals, "income", METRIC_PERIOD_YEAR);
      return shiftAndMap(raw, (e) => {
        if (isRevenue) {
          return e.revenue != null && isFinite(e.revenue) ? e.revenue / 1e9 : undefined;
        }
        return e.eps != null && isFinite(e.eps) ? e.eps : undefined;
      });
    }
    const isRevenue = metricKey === "revenue";
    const src = fundamentals.earnings || fundamentals.earningsHistory || [];
    const pts = [];
    src.forEach((e) => {
      const hasVal = isRevenue
        ? e.revenue != null && isFinite(e.revenue)
        : e.eps != null && isFinite(e.eps);
      if (!hasVal || !(e.fillingDate || e.acceptedDate || e.date)) return;
      const d = new Date(e.fillingDate || e.acceptedDate || e.date);
      d.setDate(d.getDate() + lagDays);
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      const [Y, M, D] = key.split("-").map(Number);
      const time = Date.UTC(Y, M - 1, D) / 1000;
      const val = isRevenue ? (e.revenue || 0) / 1e9 : e.eps || 0;
      pts.push({ time, value: val });
    });
    return cropToPrice(pts.sort((a, b) => a.time - b.time));
  }

  if (opt.source === "combined") {
    // Income-statement actuals (aligned with Revenue / EPS overlays — filing/report dates).
    const isRevenue = metricKey === "revenue_estimate";
    const src = resolveMetricStatementRows(fundamentals, "income", period);
    const pts = [];
    src.forEach((e) => {
      const hasVal = isRevenue
        ? e.revenue != null && isFinite(e.revenue)
        : e.eps != null && isFinite(e.eps);
      if (!hasVal || !(e.fillingDate || e.acceptedDate || e.date)) return;
      const d = new Date(e.fillingDate || e.acceptedDate || e.date);
      d.setDate(d.getDate() + lagDays);
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      const [Y, M, D] = key.split("-").map(Number);
      const time = Date.UTC(Y, M - 1, D) / 1000;
      const val = isRevenue ? (e.revenue || 0) / 1e9 : e.eps || 0;
      pts.push({ time, value: val });
    });
    return cropToPrice(pts.sort((a, b) => a.time - b.time));
  }

  if (opt.source === "dcf") {
    const dcfRes = fundamentals.dcf || [];
    return cropToPrice(
      dcfRes
        .map((e) => {
          const d = new Date(e.fillingDate || e.acceptedDate || e.date);
          d.setDate(d.getDate() + lagDays);
          const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
          const [Y, M, D] = key.split("-").map(Number);
          const time = Date.UTC(Y, M - 1, D) / 1000;
          const v = e.dcfPerShare ?? e.dcf;
          return v != null ? { time, value: v } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.time - b.time)
    );
  }

  const raw = resolveMetricStatementRows(fundamentals, opt.source, period);
  return shiftAndMap(raw, (e) => {
    if (opt.source === "balanceSheet") return balanceSheetMetricValue(e, opt.field);
    const rawVal = e[opt.field] ?? (opt.fieldAlt ? e[opt.fieldAlt] : undefined);
    return scaleFundamentalFieldValue(opt.field, rawVal);
  });
}

/**
 * Pearson correlation between metric series and price series (aligned by time).
 * @param {{ time: number, value: number }[]} metricPoints
 * @param {{ time: number, value: number }[]} pricePoints
 * @returns {number | null}
 */
export function calculateCorrelation(metricPoints, pricePoints) {
  const aligned = metricPoints
    .map((p) => {
      const y = findNearestPriceValue(p.time, pricePoints);
      return y != null ? [p.value, y] : null;
    })
    .filter(Boolean);
  if (aligned.length < 2) return null;
  const xs = aligned.map((a) => a[0]);
  const ys = aligned.map((a) => a[1]);
  const mean = (arr) => arr.reduce((s, v) => s + v, 0) / arr.length;
  const mX = mean(xs), mY = mean(ys);
  const cov = xs.reduce((s, x, i) => s + (x - mX) * (ys[i] - mY), 0);
  const sd = (arr) => Math.sqrt(arr.reduce((s, v) => s + (v - mean(arr)) ** 2, 0));
  const sX = sd(xs), sY = sd(ys);
  return sX && sY ? cov / (sX * sY) : null;
}

/**
 * Compute correlation of each fundamental metric with price (all, 10y, 5y, 3y, 1y).
 * Same structure as Fundamentals tab so UI can show the same table.
 * @param {Record<string, any>} fundamentals - API response (income, cashflow, metrics, earningsHistory, dcf, etc.)
 * @param {{ time: number, value: number }[]} pricePoints - ascending by time
 * @param {{ interval?: string, lagDays?: number }} options
 * @returns {{ metricKey: string, label: string, all: number|null, ten: number|null, five: number|null, three: number|null, one: number|null }[]}
 */
export function computeCorrelationsForFundamentals(fundamentals, pricePoints, options = {}) {
  const {
    interval = "quarter",
    lagDays = 45,
    aaaYieldPoints = [],
    fallbackYieldPercent = 5,
  } = options;
  const minPxTime = pricePoints?.length ? pricePoints[0].time : 0;
  const maxPxTime = pricePoints?.length ? pricePoints[pricePoints.length - 1].time : null;
  const opts = { interval, lagDays, minPxTime, maxPxTime, aaaYieldPoints, fallbackYieldPercent };

  const lastN = (mPtsArr, yrs) => {
    const cutoff =
      pricePoints?.length
        ? pricePoints[pricePoints.length - 1].time - yrs * 365 * 24 * 3600
        : 0;
    const mp = (mPtsArr || []).filter((p) => p.time >= cutoff);
    const pp = (pricePoints || []).filter((p) => p.time >= cutoff);
    return mp.length >= 2 && pp.length >= 2 ? calculateCorrelation(mp, pp) : null;
  };

  const results = [];
  for (const mk of CORRELATION_METRIC_KEYS) {
    const mPtsFor = buildMetricPointsForKey(mk, fundamentals, opts);
    const all =
      mPtsFor.length >= 2 && pricePoints?.length >= 2
        ? calculateCorrelation(mPtsFor, pricePoints)
        : null;
    results.push({
      metricKey: mk,
      label: METRIC_OPTIONS[mk]?.label || mk,
      all,
      ten: lastN(mPtsFor, 10),
      five: lastN(mPtsFor, 5),
      three: lastN(mPtsFor, 3),
      one: lastN(mPtsFor, 1),
    });
  }
  return results;
}
